import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
  Signal,
} from '@angular/core';
import { CardModule } from 'primeng/card';
import { WarehouseProductChangedBusEvent } from '../../bus/events/warehouse-product-changed-bus.event';
import { WarehouseProductEventBusService } from '../../bus/services/warehouse-product-event-bus.service';
import { WarehouseProductListComponent } from '../warehouse-product-list/warehouse-product-list.component';
import { TableService } from '@web/shared/ui';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Permission, PermissionService } from '@web/shared/module/auth';
import { Guid, isNotNil, Nillable } from '@web/utils';
import { Tab, TabList, TabPanel, TabPanels, Tabs } from 'primeng/tabs';
import { WarehouseOwnerDetails } from '../../models/warehouse-owner-details';
import { WarehouseOwnerDataSourceService } from '../../services/warehouse-owner-data-source.service';

@Component({
  selector: 'app-warehouse-product-tabs',
  imports: [
    CardModule,
    Tabs,
    TabList,
    Tab,
    TabPanel,
    TabPanels,
    WarehouseProductListComponent,
  ],
  templateUrl: './warehouse-product-tabs.component.html',
  styleUrl: './warehouse-product-tabs.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TableService],
})
export class WarehouseProductTabsComponent implements OnInit {
  /* prettier-ignore */
  private readonly warehouseProductEventBus = inject(WarehouseProductEventBusService);
  /* prettier-ignore */
  private readonly warehouseOwnerDataSource = inject(WarehouseOwnerDataSourceService);
  private readonly table = inject(TableService);
  private readonly permission = inject(PermissionService);
  private readonly changeDetector = inject(ChangeDetectorRef);

  public readonly downloadQuantityReportVisibility =
    this.createDownloadQuantityReportVisibilitySignal();

  public readonly owner = input.required<string>();
  public readonly owners = input.required<WarehouseOwnerDetails[]>();
  public readonly selectedOwner = signal<Nillable<string>>(null);

  public readonly changeOwnerEvent = output<Guid>();

  protected readonly isNotNil = isNotNil;

  private readonly destroyRef = inject(DestroyRef);

  public constructor() {
    this.observeBusEvents();
  }

  public ngOnInit(): void {
    this.selectedOwner.set(this.owner());
  }

  protected onTabChange(value: string | number | undefined): void {
    if (!isNotNil(value)) {
      return;
    }

    this.selectedOwner.set(String(value));

    this.changeDetector.detectChanges();

    this.changeOwnerEvent.emit(String(value));
  }

  private observeBusEvents(): void {
    this.warehouseProductEventBus
      .observeMultiple([WarehouseProductChangedBusEvent])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.table.refresh());
  }

  private createDownloadQuantityReportVisibilitySignal(): Signal<boolean> {
    return signal(
      this.permission.hasPermission(
        Permission.WAREHOUSE_CAN_DOWNLOAD_GOOD_QUANTITY_REPORT,
      ),
    );
  }
}
